import { MultiCollapsible, MultiSelect, SingleSelect } from "@bonliva-crm/ui";
import {
  ProfessionFilterModalInnerProps,
  encodeValue,
  toDecodedLabelValue,
  getProfessionToValue,
  renderSelectType,
  Option
} from "../../utils";
import { ComponentProps, useMemo, useState } from "react";
import { useGlobalStore } from "@hooks";
import locale from "@bonliva-crm/locale";
import { SelectType } from "@bonliva-crm/shared";
import { ExperienceAndJobofferFilter } from "./common/experience-and-joboffer-filter";
import {
  baseOnChange,
  commonCollapsibleProps,
  isCollapsed,
  filterOption,
  isOptionStandAlone,
  onCollapse
} from "./utils";
import { isFilterSelected } from "../../../../utils/is-filter-selected";
import { isGroupSelected } from "./utils/psykolog-utils";
import { sortBy } from "lodash";

export const SocionomFilter = (props: ProfessionFilterModalInnerProps) => {
  const store = useGlobalStore();
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const professions = props?.value?.professions;

  const professionObject = useMemo(() => {
    return props?.value?.[getProfessionToValue(professions?.[0])];
  }, [props?.value, professions]);

  if (
    !("specializations" in professionObject) ||
    !("experienceMinYears" in professionObject) ||
    !("educations" in professionObject)
  ) {
    return null;
  }

  const valueSpec = professionObject.specializations.value;
  const typeSpec = professionObject.specializations.type;
  const valueEdu = professionObject.educations;
  const valueMinExp = professionObject.experienceMinYears;

  const areaOfExpertise = store.cache.getAreaOfExpOnProfessions(
    props?.value?.professions
  );

  const options: Option[] = [];

  areaOfExpertise.forEach((aoe) => {
    const specializationsToOptions = aoe.Specializations.map((spec) => ({
      label: spec,
      value: encodeValue(aoe.Name, spec)
    }));

    options.push({
      label: aoe.Name,
      value: aoe.Name
    });

    sortBy(specializationsToOptions, "label").forEach((spec) => options.push(spec));
  });

  const onChange = baseOnChange(options, (finalValues) => {
    props.onChange({
      ...props?.value,
      [getProfessionToValue(professions?.[0])]: {
        educations: valueEdu,
        experienceMinYears: valueMinExp,
        specializations: {
          value: finalValues.map((x) => x.value),
          type: typeSpec
        }
      },
      experience: !!finalValues?.length,
      interested: finalValues.length > 0 ? "true" : "null",
      experienced: finalValues.length > 0 ? "true" : "null"
    });
  });

  const multiSelectProps: ComponentProps<typeof MultiCollapsible<Option>> = {
    ...commonCollapsibleProps,
    inputLabel: locale.Specialization,
    placeholder: null,
    options,
    value: toDecodedLabelValue(valueSpec),
    onChange,
    isOptionSelected: isGroupSelected(valueSpec, options, true),
    isIndeterminate: isGroupSelected(valueSpec, options, false),
    filterOption: filterOption(collapsedGroups, false, options),
    isCollapsed: isCollapsed(collapsedGroups),
    isOptionStandAlone: isOptionStandAlone(options),
    onCollapse: onCollapse(collapsedGroups, setCollapsedGroups),
    showBadge: false,
    isClearable: isFilterSelected(props?.value?.socialWorkerForm.specializations),
    onClear: () => {
      props.onChange({
        ...props?.value,
        socialWorkerForm: {
          ...props?.value?.socialWorkerForm,
          specializations: {
            type: SelectType.ANY,
            value: []
          }
        },
        experienced: null,
        interested: null
      });
    },
    menuChildren: {
      prependedChildren: renderSelectType(typeSpec, (t: SelectType) => {
        props.onChange({
          ...props?.value,
          [getProfessionToValue(professions?.[0])]: {
            educations: valueEdu,
            experienceMinYears: valueMinExp,
            specializations: {
              value: valueSpec,
              type: t
            }
          }
        });
      })
    }
  };

  const experienceYearFromOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].toLabelValue(
    (x) => `${x.toString()}+`,
    (x) => x.toString()
  );

  const singleSelectExperienceYearFrom: ComponentProps<typeof SingleSelect<Option>> = {
    options: experienceYearFromOptions,
    value: experienceYearFromOptions.find((x) => x.value === valueMinExp) ?? null,
    inputLabel: locale.ExperienceYearFrom,
    placeholder: null,
    onChange: (selected) => {
      props.onChange({
        ...props?.value,
        [getProfessionToValue(professions?.[0])]: {
          educations: valueEdu,
          experienceMinYears: selected.value,
          specializations: {
            value: valueSpec,
            type: typeSpec
          }
        }
      });
    },
    showBadge: false,
    isClearable: isFilterSelected(props?.value?.socialWorkerForm.experienceMinYears),
    onClear: () => {
      props.onChange({
        ...props?.value,
        socialWorkerForm: {
          ...props?.value?.socialWorkerForm,
          experienceMinYears: null
        },
        experienced: null,
        interested: null
      });
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: true,
    closeMenuOnSelect: true,
    isSearchable: false,
    maxMenuHeight: 600,
    optionType: "radio",
    menuPlacement: "auto",
    styleProps: { menu: { showBorder: true } }
  };

  const educationOptions = store.cache
    .getEducationsOnProfession(professions?.[0])
    .toLabelValue();

  const multiSelectEducationProps: ComponentProps<typeof MultiSelect<Option>> = {
    hideSelectedOptions: false,
    controlShouldRenderValue: true,
    hidePlaceholderOnFocus: true,
    optionType: "checkbox",
    showOptionDivider: false,
    isSearchable: true,
    maxMenuHeight: 600,
    closeMenuOnSelect: false,
    menuPlacement: "bottom",
    styleProps: {
      menu: {
        position: "absolute",
        showBorder: true
      }
    },
    options: educationOptions,
    value: valueEdu.toLabelValue(),
    placeholder: null,
    inputLabel: locale.Education,
    onChange: (selected) => {
      props.onChange({
        ...props?.value,
        [getProfessionToValue(professions?.[0])]: {
          experienceMinYears: valueMinExp,
          specializations: {
            value: valueSpec,
            type: typeSpec
          },
          educations: selected.map((x) => x.value)
        }
      });
    },
    showBadge: false,
    isClearable: isFilterSelected(props?.value?.socialWorkerForm.educations),
    onClear: () => {
      props.onChange({
        ...props?.value,
        socialWorkerForm: {
          ...props?.value?.socialWorkerForm,
          educations: []
        }
      });
    }
  };

  return (
    <>
      <MultiCollapsible<Option> {...multiSelectProps} />
      <MultiSelect<Option> {...multiSelectEducationProps} />
      <SingleSelect<Option> {...singleSelectExperienceYearFrom} />
      {(valueSpec.length > 0 || !!valueMinExp) && (
        <ExperienceAndJobofferFilter {...props} />
      )}
    </>
  );
};
